import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import SlideButton from "./SlideButton";
import SliderItem from "./SliderItem";
import {
  getItemsPerRow,
  isMobile,
  unwrapStyles,
  getDeviceImageSrc,
} from "../utils.js";

import range from "lodash/range";
import throttle from "lodash/throttle";
import get from "lodash/get";
import isEqual from "lodash/isEqual";

import settings from "../settings";

// import { debounce } from "lodash/debounce";

const Container = styled.div`
  overflow: hidden;
`;

const SkeletonLoader = styled.div`
  overflow: hidden;
  display: inline-block;
  position: relative;
  aspect-ratio: ${({ theme }) => theme.image.aspectRatio};
  border-radius: ${({ theme }) => theme.image.borderRadius};
  margin-left: ${({ type }) => (type === "image" ? "10px" : "0")};
  width: 100%;

  ${({ theme }) => unwrapStyles(theme.skeleton)}

  ${({ type }) =>
    type === "title" &&
    css`
      height: 20px;
      min-width: 230px;
      margin-left: 9px;
    `}

  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 25%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: shimmer 2s infinite;
    content: "";
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: ${({ theme }) => `${theme.sliderHeader.margin}`};
  ${({ theme }) =>
    theme.widgetWidth < 800 &&
    css`
      margin: ${theme.sliderHeaderSmall.margin};
      ${isMobile() && `margin: ${theme.sliderHeaderMobile.margin};`};
    `}
`;

const MoreButton = styled.a`
  ${({ theme }) => {
    return unwrapStyles(theme.moreBtn);
  }}

  white-space: nowrap;

  ${({ moreBtnHover }) =>
    moreBtnHover &&
    css`
      &:hover {
        border: 1px solid ${({ theme }) => theme.moreBtnHover.color};
        color: ${({ theme }) => theme.moreBtnHover.color};
        filter: brightness(1.2);
        transition: border-color 0.3s ease, color 0.3s ease;
      }
    `}
  }}

  ${({ theme }) =>
    theme.widgetWidth < 800 &&
    `
    font-size: 13px;
    padding: 6px 20px;
  `}

  ${({ theme }) =>
    theme.widgetWidth < 500 &&
    `
    font-size: 10px;
    padding: 5px 15px 5px 15px;
  `}
`;

const Title = styled.div`
  ${({ theme }) => unwrapStyles(theme.sliderTitle)}
  ${({ theme }) =>
    theme.widgetWidth < 800 &&
    css`
      font-size: 16px;
    `}
`;

const SliderWrapper = styled.div`
  position: relative;
  margin-bottom: 35px;
  padding: ${({ theme }) => theme.slider?.padding ?? "0 60px"}
    ${({ theme }) =>
      theme.widgetWidth < 800 &&
      css`
        padding: ${isMobile()
          ? theme.sliderMobile?.padding ?? "0 5px"
          : theme.sliderSmall?.padding ?? "0 35px"};
        margin-bottom: 25px;
      `};
`;

const SliderList = styled.div`
  display: flex;
  transition: transform 300ms ease 100ms;
  z-index: 1;
  width: 100%;
  height: 100%;
  position: relative;
  left: 0px;

  ${isMobile() === true &&
  css`
    overflow-x: scroll;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const RowSet = styled.div`
  flex: 0 0
    calc(
      ${({ itemsPerRow }) =>
        `(100% - ${itemsPerRow * 5 * 2}px) / ${itemsPerRow}`}
    );
  margin: 0 5px;
  min-width: 0;
`;

const isItemInViewPort = (index, viewed, totalInViewport) => {
  const start = viewed;
  const end = viewed + totalInViewport - 1;
  return index >= start && index <= end;
};

const Slider = ({
  title,
  items,
  showCount,
  width,
  rows,
  onItemClick,
  onViewportChange,
  maxItemsPerRow,
  isLoading,
}) => {
  const itemRef = useRef(null);
  const currentStep = useRef(0);
  const [viewed, setViewed] = useState(0);
  const prevGamesInViewport = useRef([]);

  const {
    components: { slider: moreBtnHover },
  } = settings;

  const itemsPerRow = getItemsPerRow(width, maxItemsPerRow);
  const totalInViewport = itemsPerRow * rows;

  useEffect(() => {
    if (isLoading || items?.length === 0) return;

    const gamesInViewport = items
      .filter((item, idx) => isItemInViewPort(idx, viewed, totalInViewport))
      .map((item) => item?.game_id);

    if (
      gamesInViewport.length > 0 &&
      !isEqual(gamesInViewport, prevGamesInViewport.current)
    ) {
      prevGamesInViewport.current = gamesInViewport;
      onViewportChange?.(gamesInViewport);
    }
  }, [items, viewed, totalInViewport, isLoading, onViewportChange]);

  useEffect(() => {
    const handleResize = throttle(() => {
      const totalSteps = Math.ceil(items?.length / totalInViewport);

      if (currentStep.current >= totalSteps) {
        currentStep.current = totalSteps - 1;
      }
    }, 250);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [items?.length, totalInViewport]);

  const handlePrev = () => {
    currentStep.current -= 1;
    setViewed(viewed - totalInViewport);
  };

  const handleNext = () => {
    currentStep.current += 1;
    setViewed(viewed + totalInViewport);
  };

  const hasPrev = currentStep.current > 0;
  const hasNext = (currentStep.current + 1) * totalInViewport < items?.length;

  //ADD ON ONWHEEL EVENT IN SLIDE WRAPPER
  // const handleHorizontalScroll = (deltaX) => {
  //   if (deltaX > 0 && hasNext) {
  //     handleNext();
  //   } else if (deltaX < 0 && hasPrev) {
  //     handlePrev();
  //   }
  // };

  // const debouncedWheelHandler = debounce((e) => {
  //   if (Math.abs(e.deltaY) === 0) {
  //     handleHorizontalScroll(e.deltaX);
  //   }
  // }, 250);

  const renderItems = () => {
    //generate the range of skeleton items for the num of rows plus the elements half seen in the page which are equal to the num of rows
    const generatedRows = rows * itemsPerRow + rows;
    const skeletonItemsRange = range(generatedRows);

    const elements = isLoading ? skeletonItemsRange : items;
    const rowSets = elements.reduce((itemsSet, item = {}, idx) => {
      const rowIdx = ~~(idx / rows);
      const isInViewport = isItemInViewPort(idx, viewed, totalInViewport);

      const { hasMultipleImageTypes } = settings;

      const imageSrc = hasMultipleImageTypes
        ? getDeviceImageSrc(item["images"])
        : get(item, "images[0].url");

      const sliderItem = imageSrc ? (
        <SliderItem
          ref={itemRef}
          item={item}
          key={item.game_id}
          imageSrc={imageSrc}
          onGameClick={onItemClick}
          isInFirstColumn={isInViewport && (rowIdx + 1) % itemsPerRow === 1}
          isInLastColumn={isInViewport && (rowIdx + 1) % itemsPerRow === 0}
          count={showCount ? idx + 1 : null}
          disableMagnifyOnHover={itemsPerRow === 1}
          position={idx + 1}
        />
      ) : null;

      const itemComponent = isLoading ? (
        <SkeletonLoader type="image" key={idx} />
      ) : (
        sliderItem
      );

      if (idx % rows === 0) {
        itemsSet.push([itemComponent]);
      } else {
        itemsSet[itemsSet.length - 1].push(itemComponent);
      }

      return itemsSet;
    }, []);

    return rowSets.map((set, idx) => (
      <RowSet key={idx} itemsPerRow={itemsPerRow}>
        {set}
      </RowSet>
    ));
  };

  return (
    <Container data-test="slider-container">
      {title && (
        <HeaderWrapper>
          <Title data-test="slider-title">
            {isLoading ? (
              <SkeletonLoader type="title" data-test="skeleton-loader" />
            ) : (
              title
            )}
          </Title>

          {isLoading ? null : (
            <MoreButton
              data-test="view-more-button"
              href="#"
              moreBtnHover={moreBtnHover}
            >
              View More
            </MoreButton>
          )}
        </HeaderWrapper>
      )}
      <SliderWrapper
      //  onWheel={debouncedWheelHandler}
      >
        <SliderList
          data-test="slider-list"
          className="sliderList"
          style={{
            transform: `translate3d(${currentStep.current * -100}%, 0, 0)`,
          }}
        >
          {renderItems()}
        </SliderList>

        {!isMobile() && !isLoading && (
          <>
            {hasPrev && <SlideButton onClick={handlePrev} type="prev" />}
            {hasNext && <SlideButton onClick={handleNext} type="next" />}
          </>
        )}
      </SliderWrapper>
    </Container>
  );
};

Slider.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  rows: PropTypes.number,
  maxItemsPerRow: PropTypes.number,
  showCount: PropTypes.bool,
  width: PropTypes.number,
  onItemClick: PropTypes.func,
  onViewportChange: PropTypes.func,
};

export default Slider;
